import { useParams } from 'react-router-dom'
import { useState } from 'react'
import { jobsApi } from '../api/client'
import { useJobPolling } from '../hooks/useJobPolling'
import JobProgress from '../components/jobs/JobProgress'
import Button from '../components/ui/Button'
import Spinner from '../components/ui/Spinner'
import { PDFDocument } from 'pdf-lib'
import JSZip from 'jszip'

export default function Job() {
  const { id } = useParams<{ id: string }>()
  const { status, error } = useJobPolling(id || null)
  const [isMerging, setIsMerging] = useState(false)

  if (!id) {
    return <div>Invalid job ID</div>
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-600">
          Error loading job: {error.message}
        </div>
      </div>
    )
  }

  if (!status) {
    return (
      <div className="max-w-2xl mx-auto flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  const mergePDFs = async (zipBlob: Blob): Promise<Blob> => {
    console.log('[mergePDFs] Starting merge...')
    console.log('[mergePDFs] ZIP blob size:', zipBlob.size, 'bytes')
    console.log('[mergePDFs] ZIP blob type:', zipBlob.type)
    
    // Load the ZIP file
    const zip = await JSZip.loadAsync(zipBlob)
    console.log('[mergePDFs] ZIP loaded, files:', Object.keys(zip.files))
    
    // Get all PDF files from the ZIP
    const pdfFiles = Object.keys(zip.files)
      .filter(filename => filename.endsWith('.pdf'))
      .sort() // Sort to maintain order (bloque_01, bloque_02, etc.)
    
    console.log('[mergePDFs] Found', pdfFiles.length, 'PDF files:', pdfFiles)
    
    if (pdfFiles.length === 0) {
      throw new Error('No PDF files found in ZIP')
    }
    
    // If only one PDF, return it directly
    if (pdfFiles.length === 1) {
      console.log('[mergePDFs] Only one PDF, returning directly')
      const pdfData = await zip.files[pdfFiles[0]].async('blob')
      return pdfData
    }
    
    // Create a new PDF document for merging
    console.log('[mergePDFs] Merging', pdfFiles.length, 'PDFs...')
    const mergedPdf = await PDFDocument.create()
    
    // Load and merge each PDF
    for (const filename of pdfFiles) {
      console.log('[mergePDFs] Processing', filename)
      const pdfData = await zip.files[filename].async('arraybuffer')
      const pdf = await PDFDocument.load(pdfData)
      const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices())
      pages.forEach(page => mergedPdf.addPage(page))
      console.log('[mergePDFs] Added', pages.length, 'pages from', filename)
    }
    
    // Save the merged PDF
    console.log('[mergePDFs] Saving merged PDF...')
    const mergedPdfBytes = await mergedPdf.save()
    console.log('[mergePDFs] Merged PDF size:', mergedPdfBytes.length, 'bytes')
    
    // Convert Uint8Array to ArrayBuffer for Blob compatibility
    const arrayBuffer = mergedPdfBytes.buffer.slice(
      mergedPdfBytes.byteOffset,
      mergedPdfBytes.byteOffset + mergedPdfBytes.byteLength
    ) as ArrayBuffer
    
    return new Blob([arrayBuffer], { type: 'application/pdf' })
  }

  const handleDownload = async () => {
    if (!status.result_path) return
    
    setIsMerging(true)
    
    try {
      console.log('[handleDownload] Starting download...')
      console.log('[handleDownload] Job ID:', id)
      console.log('[handleDownload] Result path:', status.result_path)
      console.log('[handleDownload] Download URL:', jobsApi.downloadUrl(id))
      
      // Download the file
      const response = await fetch(jobsApi.downloadUrl(id))
      console.log('[handleDownload] Response status:', response.status)
      console.log('[handleDownload] Response headers:', Object.fromEntries(response.headers.entries()))
      
      const blob = await response.blob()
      console.log('[handleDownload] Blob size:', blob.size, 'bytes')
      console.log('[handleDownload] Blob type:', blob.type)
      
      // IMPROVED ZIP detection: check multiple indicators
      const isZip = 
        blob.type === 'application/zip' || 
        blob.type === 'application/x-zip-compressed' ||
        blob.type === 'application/octet-stream' ||
        status.result_path.endsWith('.zip') ||
        status.result_path.includes('.zip') ||
        status.result_path.includes('zip')
      
      console.log('[handleDownload] Is ZIP?', isZip)
      
      if (isZip) {
        console.log('[handleDownload] Detected ZIP file, merging PDFs...')
        
        // Merge PDFs
        const mergedBlob = await mergePDFs(blob)
        
        console.log('[handleDownload] Merge complete, downloading merged PDF...')
        
        // Download the merged PDF
        const url = window.URL.createObjectURL(mergedBlob)
        const a = document.createElement('a')
        a.href = url
        a.download = `mpcfill_${id}_merged.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        
        console.log('[handleDownload] Merged PDF downloaded successfully')
      } else {
        console.log('[handleDownload] Single PDF detected, downloading directly')
        
        // Single PDF - download directly
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `mpcfill_${id}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        
        console.log('[handleDownload] PDF downloaded successfully')
      }
    } catch (err) {
      console.error('[handleDownload] Error downloading/merging PDF:', err)
      alert('Error al descargar el PDF. Por favor, inténtalo de nuevo.')
    } finally {
      setIsMerging(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Job Details</h1>
        <p className="text-gray-600 mt-2">Job ID: {id}</p>
      </div>

      <JobProgress status={status} />

      {status.status === 'completed' && status.result_path && (
        <div className="flex justify-center">
          <Button 
            size="lg" 
            onClick={handleDownload}
            disabled={isMerging}
          >
            {isMerging ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Fusionando PDFs...
              </>
            ) : (
              'Download PDF'
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
